export type LandingThemePalette = {
  id: string;
  name: string;
  emoji: string;
  tagline: string;
  isDark: boolean;
  /** @deprecated use !isDark */ light?: boolean;
  // Core palette
  background: string;
  surface: string;
  accent: string;
  accentSoft: string;
  border: string;
  text: string;
  highlight: string;
  // Extended — fed into CSS custom properties via inline style on .landing-shell
  textMuted: string;
  navBg: string;
  navBorder: string;
  navLinkColor: string;
  cardBg: string;
  cardBorder: string;
  tagBg: string;
  tagText: string;
  glow: string;
  /** Fullscreen atmospheric overlay gradient */
  ambient: string;
};

export const landingThemes: LandingThemePalette[] = [
  // ── 0 ─────────────────────────────────────────────────────────────
  {
    id: 'harbor-crew',
    name: 'Harbor Crew',
    emoji: '⚓',
    tagline: 'Salt air and no mercy.',
    isDark: true,
    background: '#0a0d12',
    surface:    '#111620',
    accent:     '#c0392b',
    accentSoft: '#8b2020',
    border:     '#2a1f1f',
    text:       '#f1f5f9',
    highlight:  '#e8c44a',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(10,13,18,0.96)',
    navBorder:     'rgba(192,57,43,0.22)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(192,57,43,0.1)',
    cardBorder:    'rgba(192,57,43,0.22)',
    tagBg:         'rgba(232,196,74,0.1)',
    tagText:       '#e8c44a',
    glow:          'rgba(192,57,43,0.5)',
    ambient:
      'radial-gradient(ellipse at 82% 100%, rgba(192,57,43,0.42) 0%, transparent 55%), radial-gradient(ellipse at 14% 0%, rgba(232,196,74,0.07) 0%, transparent 46%)',
  },

  // ── 1 ─────────────────────────────────────────────────────────────
  {
    id: 'dream-market',
    name: 'Dream Market',
    emoji: '🏺',
    tagline: 'Spice, stone, and afternoon light.',
    isDark: false,
    light:  true,
    background: '#faf4e8',
    surface:    '#f0e6d0',
    accent:     '#c0622a',
    accentSoft: '#e07040',
    border:     '#d4b896',
    text:       '#1a1a2e',
    highlight:  '#8b4513',
    textMuted:     'rgba(26,26,46,0.4)',
    navBg:         'rgba(255,255,255,0.7)',
    navBorder:     'rgba(212,184,150,0.4)',
    navLinkColor:  'rgba(26,26,46,0.65)',
    cardBg:        'rgba(255,255,255,0.5)',
    cardBorder:    'rgba(212,184,150,0.35)',
    tagBg:         'rgba(192,98,42,0.1)',
    tagText:       '#c0622a',
    glow:          'rgba(192,98,42,0.38)',
    ambient:
      'radial-gradient(ellipse at 50% 110%, rgba(192,98,42,0.22) 0%, transparent 55%), radial-gradient(ellipse at 80% -10%, rgba(212,184,150,0.28) 0%, transparent 50%)',
  },

  // ── 2 ─────────────────────────────────────────────────────────────
  {
    id: 'sunset-drift',
    name: 'Sunset Drift',
    emoji: '🌅',
    tagline: 'The horizon catches fire.',
    isDark: true,
    background: '#1a0e05',
    surface:    '#251508',
    accent:     '#e8734a',
    accentSoft: '#b85030',
    border:     '#3d2010',
    text:       '#f1f5f9',
    highlight:  '#f0b86e',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(26,14,5,0.96)',
    navBorder:     'rgba(232,115,74,0.22)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(232,115,74,0.1)',
    cardBorder:    'rgba(232,115,74,0.22)',
    tagBg:         'rgba(240,184,110,0.1)',
    tagText:       '#f0b86e',
    glow:          'rgba(232,115,74,0.44)',
    ambient:
      'radial-gradient(ellipse at 50% -5%, rgba(240,184,110,0.35) 0%, transparent 52%), radial-gradient(ellipse at 50% 108%, rgba(232,115,74,0.2) 0%, transparent 52%)',
  },

  // ── 3 — DEFAULT ───────────────────────────────────────────────────
  {
    id: 'midnight-navigator',
    name: 'Midnight Navigator',
    emoji: '🧭',
    tagline: 'Chart the dark. Trust the stars.',
    isDark: true,
    background: '#080c1a',
    surface:    '#0d1428',
    accent:     '#4a9eff',
    accentSoft: '#2a6ccc',
    border:     '#1a2540',
    text:       '#f1f5f9',
    highlight:  '#f5e642',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(8,12,26,0.98)',
    navBorder:     'rgba(74,158,255,0.25)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(74,158,255,0.1)',
    cardBorder:    'rgba(245,230,66,0.2)',
    tagBg:         'rgba(245,230,66,0.1)',
    tagText:       '#f5e642',
    glow:          'rgba(74,158,255,0.5)',
    ambient:
      'radial-gradient(ellipse at 62% 38%, rgba(74,158,255,0.35) 0%, transparent 55%), radial-gradient(ellipse at 82% 12%, rgba(245,230,66,0.12) 0%, transparent 38%)',
  },

  // ── 4 ─────────────────────────────────────────────────────────────
  {
    id: 'noir-harbor',
    name: 'Noir Harbor',
    emoji: '🕵️',
    tagline: 'No witnesses. No traces.',
    isDark: true,
    background: '#080808',
    surface:    '#111111',
    accent:     '#8b0000',
    accentSoft: '#600000',
    border:     '#1e1e1e',
    text:       '#f1f5f9',
    highlight:  '#c0c0c0',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(8,8,8,0.99)',
    navBorder:     'rgba(139,0,0,0.25)',
    navLinkColor:  'rgba(241,245,249,0.5)',
    cardBg:        'rgba(139,0,0,0.1)',
    cardBorder:    'rgba(139,0,0,0.22)',
    tagBg:         'rgba(192,192,192,0.08)',
    tagText:       '#c0c0c0',
    glow:          'rgba(139,0,0,0.44)',
    ambient:
      'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.72) 100%), radial-gradient(ellipse at 70% 82%, rgba(139,0,0,0.25) 0%, transparent 42%)',
  },

  // ── 5 ─────────────────────────────────────────────────────────────
  {
    id: 'silk-road',
    name: 'Silk Road',
    emoji: '💰',
    tagline: 'Dust and gold across every border.',
    isDark: true,
    background: '#0f0a02',
    surface:    '#1a1200',
    accent:     '#d4a017',
    accentSoft: '#a07010',
    border:     '#2d2200',
    text:       '#f1f5f9',
    highlight:  '#f0e68c',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(15,10,2,0.97)',
    navBorder:     'rgba(212,160,23,0.22)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(212,160,23,0.1)',
    cardBorder:    'rgba(240,230,140,0.15)',
    tagBg:         'rgba(240,230,140,0.08)',
    tagText:       '#f0e68c',
    glow:          'rgba(212,160,23,0.5)',
    ambient:
      'radial-gradient(ellipse at 50% -5%, rgba(240,230,140,0.4) 0%, transparent 52%), radial-gradient(ellipse at 50% 108%, rgba(160,90,0,0.32) 0%, transparent 50%)',
  },

  // ── 6 ─────────────────────────────────────────────────────────────
  {
    id: 'neon-dockhand',
    name: 'Neon Dockhand',
    emoji: '⚡',
    tagline: 'Third shift. The city never sleeps.',
    isDark: true,
    background: '#0a0a0a',
    surface:    '#141414',
    accent:     '#ff6b00',
    accentSoft: '#cc5000',
    border:     '#1f1f1f',
    text:       '#f1f5f9',
    highlight:  '#ff3cac',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(10,10,10,0.98)',
    navBorder:     'rgba(255,107,0,0.25)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(255,107,0,0.1)',
    cardBorder:    'rgba(255,60,172,0.15)',
    tagBg:         'rgba(255,107,0,0.1)',
    tagText:       '#ff3cac',
    glow:          'rgba(255,107,0,0.5)',
    ambient:
      'radial-gradient(ellipse at 80% 96%, rgba(255,107,0,0.3) 0%, transparent 48%), radial-gradient(ellipse at 18% 15%, rgba(255,60,172,0.15) 0%, transparent 42%)',
  },

  // ── 7 ─────────────────────────────────────────────────────────────
  {
    id: 'jungle-captain',
    name: 'Jungle Captain',
    emoji: '🌿',
    tagline: 'Deep canopy. Cargo moving unseen.',
    isDark: true,
    background: '#030f05',
    surface:    '#071a09',
    accent:     '#2ecc71',
    accentSoft: '#1a9950',
    border:     '#0f2d12',
    text:       '#f1f5f9',
    highlight:  '#a8ff3e',
    textMuted:     'rgba(241,245,249,0.4)',
    navBg:         'rgba(3,15,5,0.97)',
    navBorder:     'rgba(46,204,113,0.25)',
    navLinkColor:  'rgba(241,245,249,0.55)',
    cardBg:        'rgba(46,204,113,0.1)',
    cardBorder:    'rgba(168,255,62,0.15)',
    tagBg:         'rgba(46,204,113,0.1)',
    tagText:       '#a8ff3e',
    glow:          'rgba(46,204,113,0.44)',
    ambient:
      'radial-gradient(ellipse at 50% 108%, rgba(3,15,5,0.8) 0%, transparent 55%), radial-gradient(ellipse at 50% -5%, rgba(46,204,113,0.2) 0%, transparent 48%)',
  },

  // ── 8 ─────────────────────────────────────────────────────────────
  {
    id: 'sahara-post',
    name: 'Sahara Post',
    emoji: '🐪',
    tagline: 'Sand, silence, and a letter from far away.',
    isDark: false,
    light:  true,
    background: '#f5ede0',
    surface:    '#ede0cc',
    accent:     '#b5541c',
    accentSoft: '#d06020',
    border:     '#d4b896',
    text:       '#1a1a2e',
    highlight:  '#1a1200',
    textMuted:     'rgba(26,26,46,0.4)',
    navBg:         'rgba(255,255,255,0.7)',
    navBorder:     'rgba(212,184,150,0.4)',
    navLinkColor:  'rgba(26,26,46,0.65)',
    cardBg:        'rgba(255,255,255,0.5)',
    cardBorder:    'rgba(212,184,150,0.35)',
    tagBg:         'rgba(181,84,28,0.1)',
    tagText:       '#b5541c',
    glow:          'rgba(181,84,28,0.38)',
    ambient:
      'radial-gradient(ellipse at 60% 110%, rgba(181,84,28,0.2) 0%, transparent 55%), radial-gradient(ellipse at 40% -10%, rgba(212,184,150,0.25) 0%, transparent 48%)',
  },

  // ── 9 ─────────────────────────────────────────────────────────────
  {
    id: 'pearl-harbor',
    name: 'Pearl Harbor',
    emoji: '🦪',
    tagline: 'Morning tide. Clean light. Something arriving.',
    isDark: false,
    light:  true,
    background: '#f0f4f8',
    surface:    '#e2eaf2',
    accent:     '#2c6fad',
    accentSoft: '#1a5090',
    border:     '#b8cfe0',
    text:       '#1a1a2e',
    highlight:  '#1a3a5c',
    textMuted:     'rgba(26,26,46,0.4)',
    navBg:         'rgba(255,255,255,0.7)',
    navBorder:     'rgba(184,207,224,0.5)',
    navLinkColor:  'rgba(26,26,46,0.65)',
    cardBg:        'rgba(255,255,255,0.6)',
    cardBorder:    'rgba(184,207,224,0.4)',
    tagBg:         'rgba(44,111,173,0.1)',
    tagText:       '#2c6fad',
    glow:          'rgba(44,111,173,0.31)',
    ambient:
      'radial-gradient(ellipse at 50% -5%, rgba(184,207,224,0.4) 0%, transparent 52%), radial-gradient(ellipse at 50% 105%, rgba(44,111,173,0.15) 0%, transparent 50%)',
  },
];
